import { type WritableDraft } from 'immer';

import { type PartialPropsOf } from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';
import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';
import { type PropertiesConfigInputFontStyleOptions } from '@blaze-shared/elements-domain';

import { type EngineState } from '../../../+state/engine';
import { createElement, type ElementHandler, findElement, getElement } from '../../../ai';
import { toHeightSettings, toWidthSettings } from '../../../ai/models/styles';

import { type TextAreaContext } from './text-area.definition';

const FONT_WEIGHT_MAP: Record<string, FontWeightOption> = {
  Thin: '100',
  Light: '300',
  Regular: '400',
  Medium: '500',
  SemiBold: '600',
  Bold: '700',
  ExtraBold: '800',
};

export class TextAreaElementHandler implements ElementHandler<'textArea'> {
  readonly type = 'textArea';

  process(context: TextAreaContext, draft: WritableDraft<EngineState>): void {
    const props = this.getTextAreaProps(context, draft);
    const result = createElement(draft, context, props, !context.element.styleName);
    const element = getElement(draft, result.elementId, 'textArea');

    element.width = toWidthSettings(context.element.width ?? { $type: 'fitContent' });
    element.height = toHeightSettings(context.element.height ?? { $type: 'fitContent' });
  }

  private getInitialProps(
    context: TextAreaContext,
    draft: WritableDraft<EngineState>,
  ): PartialPropsOf<'textArea'> {
    const element = findElement<'textArea'>(draft, context.element.id);
    return element?.props ?? {};
  }

  private getTextAreaProps(
    context: TextAreaContext,
    draft: WritableDraft<EngineState>,
  ): PartialPropsOf<'textArea'> {
    let props = this.getInitialProps(context, draft);

    if (context.element.styleName) {
      props = applyPropsPatch(props, {
        styleConfig: {
          styleName: context.element.styleName,
        },
      });
    } else {
      props = applyPropsPatch(props, {
        content: {
          font: {
            style: this.getFontStyle(context),
            fontWeight: this.getFontWeight(context),
          },
        },
      });
    }

    if (context.element.textBehavior) {
      props = applyPropsPatch(props, {
        content: {
          container: {
            overflow: this.getTextBehavior(context),
          },
        },
      });
    }

    if (context.element.styles?.font) {
      props = applyPropsPatch(props, {
        content: {
          font: {
            fontFamily: context.element.styles.font,
          },
        },
      });
    }

    if (context.element.styles?.fontSize) {
      props = applyPropsPatch(props, {
        content: {
          font: {
            fontSize: `${context.element.styles.fontSize}px`,
          },
        },
      });
    }

    if (context.element.styles?.lineHeight) {
      props = applyPropsPatch(props, {
        content: {
          font: {
            lineHeight: `${context.element.styles.lineHeight}px`,
          },
        },
      });
    }

    if (context.element.text) {
      props = applyPropsPatch(props, {
        content: {
          input: {
            value: context.element.text,
          },
        },
      });
    }

    return props;
  }

  private getFontStyle(context: TextAreaContext): PropertiesConfigInputFontStyleOptions {
    const styles: PropertiesConfigInputFontStyleOptions = [];

    if (context.element.styles?.italic) {
      styles.push('italic');
    }

    if (context.element.styles?.underline) {
      styles.push('underline');
    }

    if (context.element.styles?.strikethrough) {
      styles.push('line-through');
    }

    return styles;
  }

  private getFontWeight(context: TextAreaContext): FontWeightOption {
    return FONT_WEIGHT_MAP[context.element.styles?.fontWeight ?? 'Regular'];
  }

  private getTextBehavior(context: TextAreaContext): 'hidden' | 'auto' {
    if (context.element.textBehavior === 'scroll') {
      return 'auto';
    } else {
      return 'hidden';
    }
  }
}
